import type { PageKeyType } from '@/constants/page.constants';

/**
 * Splits a URL into its base path and hash components.
 *
 * Takes a URL string that may contain a hash (#) and separates it into
 * the base URL path and the hash value. If no hash is present, returns
 * the full URL as finalURL and an empty string as hash.
 * @param url The URL string to parse (e.g., "/mobilephone#mobile-plan")
 */
export function getHashAndStripUrl(url: string): {
  finalURL: string;
  hash: string;
} {
  const hashIndex = url.indexOf('#');

  if (hashIndex === -1) {
    return {
      finalURL: url,
      hash: '',
    };
  }

  const finalURL = url.substring(0, hashIndex);
  const hash = url.substring(hashIndex + 1);

  return {
    finalURL,
    hash: hash,
  };
}

export function containsURI(text: string) {
  const uriPatterns = [
    /mailto:/i, // Email links
    /tel:/i, // Telephone links
  ];

  return uriPatterns.some((pattern) => pattern.test(text));
}

/**
 * Builds the order form URL with the appropriate query parameters based on the selected offers.
 *
 * Mobile offers only require mobilePlanId.
 * Broadband offers require ALL three parameters (planId, provider, and locationId).
 * Both offers can be combined.
 */
export function buildOrderFormUrlByOffers({
  mobilePlanId,
  broadbandPlanId,
  broadbandProvider,
  broadbandLocationId,
  agentToken,
  conversationId,
}: {
  mobilePlanId: number | undefined;
  broadbandPlanId: number | undefined;
  broadbandProvider?: string;
  broadbandLocationId?: string;
  agentToken?: string;
  conversationId?: string;
}) {
  const orderFormUrl = import.meta.env.VITE_ORDERFORM_URL;
  const params = new URLSearchParams();
  if (mobilePlanId) {
    params.append('mobilePlanId', mobilePlanId.toString());
  }
  if (broadbandPlanId && broadbandProvider && broadbandLocationId) {
    params.append('broadbandPlanId', broadbandPlanId.toString());
    params.append('broadbandProvider', broadbandProvider);
    params.append('broadbandLocationId', broadbandLocationId);
  }

  if (agentToken) {
    params.append('agent-token', agentToken);
  }

  if (conversationId) {
    params.append('conversation_id', conversationId);
  }
  //TODO: We should throw an error + log to Sentry if for some reason we don't have all the required params to build a broadband order form url

  return `${orderFormUrl}?${params.toString()}`;
}

export const shouldOnClickServiceableNavigate = (pathName: PageKeyType) =>
  pathName !== '/broadband/nbn' && pathName !== '/';

export const shouldOnClickAlreadyHasServiceNavigate = (pathName: PageKeyType) =>
  pathName !== '/mobilephone' && pathName !== '/';
